<template>
  <div ref="rootEl"
    class="min-h-screen overflow-x-hidden bg-gradient-to-b from-sky-50 via-white to-sky-100 dark:from-[#020617] dark:via-[#020617] dark:to-[#020617] text-slate-900 dark:text-slate-50">
    <HeaderNav />
    <div aria-hidden="true" class="h-[72px] md:h-[76px]"></div>

    <main class="mx-auto max-w-full px-4 sm:px-6 lg:px-8 overflow-x-visible">
      <section :class="[
        'hero-section relative overflow-visible text-slate-900 dark:text-slate-50 reveal',
        heroReady && 'is-ready'
      ]">
        <div class="pointer-events-none absolute inset-0">
          <div class="hero-glow"></div>
          <div
            class="hidden md:block absolute -right-32 -top-40 h-80 w-80 rounded-full bg-sky-300/40 blur-3xl dark:bg-sky-500/25">
          </div>
          <div
            class="hidden md:block absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-cyan-300/40 blur-3xl dark:bg-cyan-500/25">
          </div>
        </div>

        <div
          class="relative mx-auto w-full max-w-6xl px-2 sm:px-4 lg:px-0 pt-16 pb-12 sm:pt-24 sm:pb-20 lg:pt-32 lg:pb-28">
          <div class="grid items-center gap-10 sm:gap-12 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,0.6fr)]">
            <div class="space-y-8 lg:space-y-10 reveal-on-scroll">
              <div class="flex justify-center sm:justify-start">
                <div
                  class="hero-tag inline-flex items-center gap-2 rounded-full border border-sky-500/20 bg-white/80 px-3 py-1 text-[11px] font-medium tracking-wide text-sky-800 shadow-sm backdrop-blur-sm dark:border-sky-300/20 dark:bg-white/5 dark:text-sky-200">
                  <span class="dot"></span>
                  <span class="label">Palette Studio · Crafted for designers</span>
                </div>
              </div>

              <div class="space-y-4 sm:space-y-5 lg:space-y-6">
                <div class="max-md:flex max-md:justify-center max-md:text-center lg:text-left">
                  <h1 class="hero-title hero-title-wrap" v-html="titleHtml"></h1>
                </div>

                <p
                  class="max-w-xl text-[13px] xs:text-[14px] sm:text-[15px] leading-relaxed text-slate-600 dark:text-slate-300 max-md:mx-auto max-md:text-center lg:text-left">
                  Build palettes, gradients,
                  and tokens that stay consistent from concept to production.
                </p>
              </div>

              <div class="flex flex-wrap items-center gap-4 pt-1 sm:pt-2 reveal-on-scroll max-md:justify-center">
                <button class="btn-primary animate-cta" @click="$router.push('/pricing')">
                  Start for free
                </button>

                <button type="button" class="btn-ghost" @click="$router.push('/app/palettes')">
                  Explore the studio
                </button>
              </div>

              <div
                class="flex flex-wrap items-center gap-4 pt-3 text-xs text-slate-500 dark:text-slate-400 max-md:justify-center max-md:text-center">
                <div class="flex -space-x-2 justify-center">
                  <span
                    class="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-cyan-400 text-[10px] font-semibold text-white ring-2 ring-slate-50 dark:ring-slate-900">
                    UI
                  </span>
                  <span
                    class="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-sky-400 text-[10px] font-semibold text-white ring-2 ring-slate-50 dark:ring-slate-900">
                    Web
                  </span>
                  <span
                    class="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-400 text-[10px] font-semibold text-white ring-2 ring-slate-50 dark:ring-slate-900">
                    Brand
                  </span>
                </div>
                <span>Built for modern product teams and solo designers.</span>
              </div>
            </div>

            <div class="relative flex justify-center lg:justify-end reveal-on-scroll mt-6 lg:mt-0">
              <div
                class="max-sm:scale-[0.96] rounded-3xl border bg-white shadow-xl backdrop-blur-xl border-slate-200/90 dark:bg-[#0c0f18]/90 dark:border-white/10 dark:shadow-[0_0_60px_rgba(0,0,0,.45)] p-6 w-[320px] sm:w-[340px]">
                <div class="flex items-center justify-between gap-3">
                  <div class="space-y-1.5">
                    <p class="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                      Active palette
                    </p>
                    <p class="text-sm sm:text-[15px] font-semibold text-slate-900 dark:text-slate-50">
                      Aurora Interface System
                    </p>
                  </div>

                  <span
                    class="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2.5 py-1 text-[10px] font-medium text-slate-800 dark:bg-white/10 dark:border-white/20 dark:text-slate-100">
                    <span class="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                    Harmonized
                  </span>
                </div>

                <div class="mt-6 grid grid-cols-4 gap-2">
                  <div class="space-y-1">
                    <div class="h-14 rounded-2xl bg-[#0F172A] shadow-sm"></div>
                    <div class="flex flex-col">
                      <span
                        class="text-[11px] font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">Primary</span>
                      <span
                        class="text-[11px] font-mono text-slate-800 dark:text-slate-400 whitespace-nowrap">#0F172A</span>
                    </div>
                  </div>

                  <div class="space-y-1">
                    <div class="h-14 rounded-2xl bg-[#0284C7] shadow-sm"></div>
                    <div class="flex flex-col">
                      <span
                        class="text-[11px] font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">Accent</span>
                      <span
                        class="text-[11px] font-mono text-slate-800 dark:text-slate-400 whitespace-nowrap">#0284C7</span>
                    </div>
                  </div>

                  <div class="space-y-1">
                    <div class="h-14 rounded-2xl bg-[#22C55E] shadow-sm"></div>
                    <div class="flex flex-col">
                      <span
                        class="text-[11px] font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">Success</span>
                      <span
                        class="text-[11px] font-mono text-slate-800 dark:text-slate-400 whitespace-nowrap">#22C55E</span>
                    </div>
                  </div>

                  <div class="space-y-1">
                    <div class="h-14 rounded-2xl bg-[#FACC15] shadow-sm"></div>
                    <div class="flex flex-col">
                      <span
                        class="text-[11px] font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">Highlight</span>
                      <span
                        class="text-[11px] font-mono text-slate-800 dark:text-slate-400 whitespace-nowrap">#FACC15</span>
                    </div>
                  </div>
                </div>

                <div class="mt-6 grid gap-3 md:grid-cols-3">
                  <div
                    class="rounded-2xl bg-slate-50 border border-slate-200 px-3 py-2.5 flex flex-col gap-0.5 dark:bg-white/5 dark:border-white/10">
                    <p class="text-[11px] font-medium text-slate-700 dark:text-slate-400">Tokens</p>
                    <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">48</p>
                  </div>

                  <div
                    class="rounded-2xl bg-slate-50 border border-slate-200 px-3 py-2.5 flex flex-col gap-0.5 dark:bg-white/5 dark:border-white/10">
                    <p class="text-[11px] font-medium text-slate-700 dark:text-slate-400">Gradients</p>
                    <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">12</p>
                  </div>

                  <div
                    class="rounded-2xl bg-slate-50 border border-slate-200 px-3 py-2.5 flex flex-col gap-0.5 dark:bg-white/5 dark:border-white/10">
                    <p class="text-[11px] font-medium text-slate-700 dark:text-slate-400">Contrast</p>
                    <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">AA / AAA</p>
                  </div>
                </div>

                <div
                  class="mt-4 flex items-center justify-between text-[11px] text-slate-600 dark:text-slate-400 gap-2 max-sm:flex-col max-sm:items-start">
                  <span>Preview in light & dark surfaces</span>

                  <span class="inline-flex items-center gap-1">
                    <span
                      class="inline-flex h-4 w-7 items-center justify-between rounded-full bg-slate-900/10 dark:bg-white/10 px-[1px]">
                      <span class="h-3 w-3 rounded-full bg-slate-900 dark:bg-slate-50"></span>
                      <span class="h-3 w-3 rounded-full bg-slate-400 dark:bg-slate-500"></span>
                    </span>
                    Synchronised
                  </span>
                </div>
              </div>

              <div
                class="pointer-events-none absolute -left-6 -bottom-10 hidden h-28 w-28 rounded-3xl bg-gradient-to-br from-sky-400/70 via-cyan-300/70 to-violet-400/70 blur-xl md:block dark:from-sky-500/60 dark:via-cyan-400/60 dark:to-violet-500/60">
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="reveal-on-scroll">
        <UiSection>
          <FramerGallery />
        </UiSection>
      </div>

      <div class="section-separator">
        <span class="material-symbols-rounded arrow-down">expand_more</span>
      </div>

      <div class="reveal-on-scroll">
        <UiSection>
          <FeatureReveal />
        </UiSection>
      </div>

      <div class="section-separator">
        <span class="material-symbols-rounded arrow-down">expand_more</span>
      </div>

      <div class="reveal-on-scroll">
        <UiSection>
          <CarrouselV1 />
        </UiSection>
      </div>

      <div class="section-separator">
        <span class="material-symbols-rounded arrow-down">expand_more</span>
      </div>

      <div class="reveal-on-scroll">
        <UiSection>
          <BlogPage />
        </UiSection>
      </div>

      <div class="section-separator">
        <span class="material-symbols-rounded arrow-down">expand_more</span>
      </div>

      <div class="reveal-on-scroll">
        <UiSection>
          <SectionAi />
        </UiSection>
      </div>
      <br />
      <br />
      <div class="section-separator">
        <span class="material-symbols-rounded arrow-down">expand_more</span>
      </div>

      <div class="reveal-on-scroll">
        <UiSection>
          <TestimonialsPage />
        </UiSection>
      </div>

      <div class="section-separator">
        <span class="material-symbols-rounded arrow-down">expand_more</span>
      </div>

      <div class="reveal-on-scroll">
        <UiSection>
          <CarrouselV2 />
        </UiSection>
      </div>

      <div class="section-separator">
        <span class="material-symbols-rounded arrow-down">expand_more</span>
      </div>

      <div class="reveal-on-scroll">
        <UiSection>
          <EndPage />
        </UiSection>
      </div>
    </main>

    <button v-if="showBackToTop" type="button" class="back-to-top" @click="scrollToTop">
      <span class="material-symbols-rounded text-[20px]">arrow_upward</span>
    </button>


    <FooterBar />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import HeaderNav from '../components/marketing/HeaderNav.vue'
import FooterBar from '../components/marketing/FooterBar.vue'
import UiSection from '../components/ui/Section.vue'
import FramerGallery from '../components/inicio/FramerGallery.vue'
import FeatureReveal from '@/components/inicio/FeatureReveal.vue'
import CarrouselV1 from '@/components/inicio/CarrouselV1.vue'
import BlogPage from '@/components/inicio/BlogPage.vue'
import CarrouselV2 from '@/components/inicio/CarrouselV2.vue'
import EndPage from '@/components/inicio/EndPage.vue'
import TestimonialsPage from '@/components/inicio/TestimonialsPage.vue'
import SectionAi from '@/components/inicio/SectionAi.vue'

const heroReady = ref(false)
const rootEl = ref(null)
let io
const showBackToTop = ref(false)

const handleScroll = () => {
  showBackToTop.value = window.scrollY > 400
}

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const wrapLetters = (txt) => {
  let i = 0
  return txt
    .split('')
    .map((ch) => {
      if (ch === ' ') return '<span class="sp">&nbsp;</span>'
      return `<span class="ch" style="--i:${i++}">${ch}</span>`
    })
    .join('')
}

const titleHtml = ref('')

onMounted(async () => {
  titleHtml.value = `
  ${wrapLetters('Palette Studio.')}
  <span class="block gradient-text">${wrapLetters('Color, but intentional.')}</span>
`

  await nextTick()
  requestAnimationFrame(() => (heroReady.value = true))

  const els = rootEl.value.querySelectorAll('.reveal-on-scroll')
  io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) e.target.classList.add('is-in')
      }
    },
    { threshold: 0.16 }
  )
  els.forEach((el) => io.observe(el))

  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  if (io) io.disconnect()
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.section-separator {
  position: relative;
  width: 100%;
  height: 0;
  margin: -24px 0 -24px;
  display: flex;
  justify-content: center;
  pointer-events: none;
  background: transparent;
  z-index: 30;
}

.section-separator .arrow-down {
  pointer-events: auto;
  position: relative;
  font-size: 34px;
  line-height: 1;
  opacity: 0.65;
  color: #94a3b8;
  animation: nexgrid-arrow 2.4s ease-in-out infinite;
  transition: opacity 0.2s ease, color 0.2s ease;
  z-index: 40;
}

.dark .section-separator .arrow-down {
  color: rgba(255, 255, 255, 0.5);
  opacity: 0.75;
}


@keyframes nexgrid-arrow {
  0% {
    transform: translateY(0);
    opacity: 0.55;
  }

  50% {
    transform: translateY(4px);
    opacity: 0.9;
  }

  100% {
    transform: translateY(0);
    opacity: 0.55;
  }
}

.back-to-top {
  position: fixed;
  right: 1.25rem;
  bottom: 1.75rem;
  z-index: 60;
  height: 44px;
  width: 44px;
  border-radius: 9999px;
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: radial-gradient(circle at 0% 0%, #e0f2fe, #0ea5e9);
  color: #0b1120;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.35);
  cursor: pointer;
  transition: transform 0.18s ease-out, box-shadow 0.18s ease-out, filter 0.18s ease-out, opacity 0.18s ease-out;
}

.dark .back-to-top {
  background: radial-gradient(circle at 0% 0%, #0f172a, #0ea5e9);
  color: #e5e7eb;
  border-color: rgba(148, 163, 184, 0.7);
}

.back-to-top:hover {
  transform: translateY(-1px) scale(1.03);
  box-shadow: 0 20px 55px rgba(15, 23, 42, 0.45);
  filter: saturate(1.05);
}

.back-to-top:active {
  transform: translateY(0) scale(0.98);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.35);
}

@media (max-width: 640px) {
  .back-to-top {
    right: 0.9rem;
    bottom: 1.25rem;
    height: 40px;
    width: 40px;
  }
}


.hero-title {
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif;
  font-weight: 800;
  line-height: 0.96;
  letter-spacing: -0.06em;
  font-size: clamp(30px, 4vw, 64px);
  background: conic-gradient(from 160deg, #0ea5e9, #22c55e, #a855f7, #0ea5e9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 10px 30px rgba(15, 23, 42, 0.28));
  opacity: 0;
  transform: translateY(22px);
  transition: all 1.1s cubic-bezier(0.19, 0.74, 0.32, 1);
  overflow: visible;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.hero-title-wrap {
  display: block;
  max-width: 16ch;
  margin-inline: auto;
}

@media (min-width: 768px) {
  .hero-title-wrap {
    max-width: 18ch;
    margin-inline: 0;
  }
}

.hero-title .ch {
  display: inline-block;
  opacity: 0;
  transform: translateY(24px) scale(0.96);
  filter: blur(10px);
  transition: transform 0.7s cubic-bezier(0.22, 0.61, 0.36, 1), opacity 0.6s ease, filter 0.7s ease;
  transition-delay: calc(var(--i) * 14ms);
}

.hero-title .sp {
  display: inline-block;
  width: 0.35em;
}

.hero-title.is-ready,
.is-ready .hero-title {
  opacity: 1;
  transform: translateY(0);
}

@media (max-width: 640px) {
  .hero-tag {
    padding: 0 10px !important;
    height: 24px !important;
    font-size: 10px !important;
    gap: 4px !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }

  .hero-tag .dot {
    display: inline-block;
    height: 6px !important;
    width: 6px !important;
    border-radius: 50%;
    background: #0ea5e9;
  }

  .hero-tag .label {
    font-size: 10px !important;
    letter-spacing: 0.12em !important;
  }
}

.hero-title.is-ready .ch,
.is-ready .hero-title .ch {
  opacity: 1;
  transform: translateY(0) scale(1);
  filter: blur(0);
}

.gradient-text {
  background: linear-gradient(120deg, #0ea5e9, #22c55e, #a855f7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  padding: 0 22px;
  height: 46px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: radial-gradient(circle at 0% 0%, #e0f2fe, #0ea5e9) padding-box;
  color: #0b1120;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.22), 0 0 0 1px rgba(8, 47, 73, 0.16);
  transition: transform 0.18s ease-out, box-shadow 0.18s ease-out, filter 0.18s ease-out, background 0.2s ease-out;
}

.btn-primary:hover {
  transform: translateY(-1px) scale(1.02);
  box-shadow: 0 22px 55px rgba(15, 23, 42, 0.28), 0 0 0 1px rgba(8, 47, 73, 0.2);
  filter: saturate(1.05);
}

.btn-primary:active {
  transform: translateY(0) scale(0.99);
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.28), 0 0 0 1px rgba(8, 47, 73, 0.24);
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 18px;
  border-radius: 9999px;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
  backdrop-filter: blur(10px);
  transition: background 0.22s ease, border-color 0.22s ease, transform 0.18s ease, box-shadow 0.18s ease;
}

.dark .btn-ghost {
  background: rgba(15, 23, 42, 0.8);
  color: #e5e7eb;
  border-color: rgba(148, 163, 184, 0.55);
}

.btn-ghost:hover {
  background: rgba(248, 250, 252, 1);
  transform: translateY(-1px);
  box-shadow: 0 14px 35px rgba(15, 23, 42, 0.2);
}

.dark .btn-ghost:hover {
  background: rgba(15, 23, 42, 0.95);
}

.hero-glow {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(800px 520px at 50% -10%, rgba(56, 189, 248, 0.25), transparent 65%),
    radial-gradient(640px 420px at 10% 100%, rgba(59, 130, 246, 0.18), transparent 65%);
  mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0.7) 60%, transparent);
}

.dark .hero-glow {
  background:
    radial-gradient(900px 540px at 50% -12%, rgba(56, 189, 248, 0.45), transparent 65%),
    radial-gradient(640px 420px at 6% 100%, rgba(59, 130, 246, 0.35), transparent 65%);
}

@media (max-width: 768px) {
  .hero-glow {
    inset: -140px -80px -140px;
    background:
      radial-gradient(780px 560px at 50% -20%, rgba(56, 189, 248, 0.35), transparent 70%),
      radial-gradient(900px 640px at 50% 120%, rgba(15, 23, 42, 0.9), transparent 82%);
    mask-image: radial-gradient(ellipse at 50% 5%,
        rgba(0, 0, 0, 1) 0%,
        rgba(0, 0, 0, 0.9) 35%,
        rgba(0, 0, 0, 0) 85%);
  }
}

.palette-card {
  position: relative;
  width: 100%;
  max-width: 360px;
  border-radius: 32px;
  padding: 20px 18px 18px;
  background:
    radial-gradient(circle at 0% 0%, rgba(148, 163, 184, 0.18), transparent 55%),
    radial-gradient(circle at 100% 100%, rgba(59, 130, 246, 0.18), transparent 55%),
    rgba(15, 23, 42, 0.02);
  border: 1px solid rgba(148, 163, 184, 0.32);
  box-shadow: 0 26px 80px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(18px);
}

.dark .palette-card {
  background:
    radial-gradient(circle at 0% 0%, rgba(148, 163, 184, 0.25), transparent 55%),
    radial-gradient(circle at 100% 100%, rgba(56, 189, 248, 0.25), transparent 55%),
    rgba(15, 23, 42, 0.95);
  border-color: rgba(148, 163, 184, 0.4);
  box-shadow: 0 28px 90px rgba(15, 23, 42, 0.9), 0 0 0 1px rgba(15, 23, 42, 0.9);
}

.swatch-column {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

@media (max-width: 640px) {
  .btn-primary {
    height: 40px;
    padding: 0 18px;
    font-size: 12px;
    letter-spacing: 0.06em;
  }

  .btn-ghost {
    height: 38px;
    padding: 0 16px;
    font-size: 12px;
  }
}


.swatch-main {
  border-radius: 18px;
  height: 70px;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.4);
}

.swatch-label {
  font-size: 11px;
  font-weight: 500;
  color: rgba(148, 163, 184, 0.95);
}

.swatch-hex {
  font-size: 11px;
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
    'Courier New', monospace;
  color: rgba(148, 163, 184, 1);
}

.mini-card {
  border-radius: 18px;
  padding: 10px 12px;
  background: linear-gradient(to bottom right, rgba(15, 23, 42, 0.02), rgba(15, 23, 42, 0.08));
  border: 1px solid rgba(148, 163, 184, 0.4);
}

.dark .mini-card {
  background: linear-gradient(to bottom right, rgba(15, 23, 42, 0.7), rgba(15, 23, 42, 0.95));
  border-color: rgba(148, 163, 184, 0.55);
}

.mini-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgba(148, 163, 184, 0.9);
}

.mini-value {
  margin-top: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #e5e7eb;
}

.reveal-on-scroll {
  opacity: 0;
  transform: translateY(18px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.reveal-on-scroll.is-in {
  opacity: 1;
  transform: translateY(0);
}

.animate-cta {
  animation: soft-pulse 2.4s ease-in-out infinite;
}

@keyframes soft-pulse {

  0%,
  100% {
    transform: translateY(0) scale(1);
    box-shadow: 0 18px 45px rgba(15, 23, 42, 0.22), 0 0 0 1px rgba(8, 47, 73, 0.16);
  }

  50% {
    transform: translateY(-1px) scale(1.015);
    box-shadow: 0 22px 60px rgba(15, 23, 42, 0.32), 0 0 0 1px rgba(8, 47, 73, 0.26);
  }
}

@media (max-width: 480px) {
  .palette-card {
    padding: 16px 14px 14px;
    border-radius: 26px;
  }

  .swatch-main {
    height: 60px;
  }

  .mini-value {
    font-size: 12px;
  }
}
</style>
